import { z } from 'zod';

/** Coerce numeric fields that may arrive as strings from Postgres */
const N = z.coerce.number();

export const BuildersListSchema = z.array(
  z
    .object({
      id: N,
      name: z.string(),
      sage_customer_id: z.string().nullable().optional(),
      notes: z.string().nullable().optional(),
      entity_id: N.nullable().optional(),
      entity_name: z.string().nullable().optional(),
      community_count: N.optional(),
      bidding_community_count: N.optional(),
      awarded_community_count: N.optional(),
      mto_count: N.optional(),
      model_count: N.optional(),
      models_missing_mto_count: N.optional(),
      total_lines: N.optional(),
    })
    .passthrough()
);

export const AcceptedBidSchema = z
  .object({
    revisionId: N,
    revision: N,
    acceptedAt: z.coerce.string().optional(),
  })
  .nullable()
  .optional();

export const CommunityScopeSchema = z.object({
  id: N,
  name: z.string(),
  sort_order: N.optional(),
});

export const CommunityRowSchema = z
  .object({
    id: N,
    builder_id: N,
    name: z.string(),
    num_lots: N.nullable().optional(),
    city: z.string().nullable().optional(),
    zip: z.string().nullable().optional(),
    quote_footer_html: z.string().nullable().optional(),
    sage_id: z.string().nullable().optional(),
    community_property_id: N.nullable().optional(),
    scopes: z.array(CommunityScopeSchema).optional(),
    mto_count: N.optional(),
    total_lines: N.optional(),
    bidLocked: z.boolean().optional(),
    acceptedBid: AcceptedBidSchema,
  })
  .passthrough();

export const CommunitiesListSchema = z.array(CommunityRowSchema);

export const MtoRowSchema = z
  .object({
    id: N,
    community_id: N,
    name: z.string(),
    filename: z.string().nullable().optional(),
    line_count: N.optional(),
    plan_count: N.optional(),
    model_id: N.nullable().optional(),
    model_name: z.string().nullable().optional(),
  })
  .passthrough();

export const MtosListSchema = z.array(MtoRowSchema);

export const BidPhaseSchema = z.object({
  material: N,
  labor: N,
  cost: N,
  items: N,
  priced: N,
});

export const BidUnpricedLineSchema = z
  .object({
    modelId: N,
    modelName: z.string(),
    itemId: z.coerce.string(),
    phaseId: z.coerce.string().optional(),
    phaseName: z.string().optional(),
    qty: N.nullable(),
    baseQty: N.nullable(),
    qtyOverride: z.boolean(),
    payableTaskGroup: z.string().nullable(),
    reason: z.string(),
    source: z.string(),
    lineId: N.nullable(),
  })
  .passthrough();

export const BidBreakdownLineSchema = z
  .object({
    itemId: z.coerce.string(),
    phaseId: z.coerce.string().optional(),
    phaseName: z.string().optional(),
    subPhaseName: z.string().nullable().optional(),
    kind: z.enum(['material', 'labor']),
    qty: N,
    baseQty: N.nullable(),
    qtyOverride: z.boolean(),
    unitPrice: N,
    supplierPrice: N.nullable(),
    priceOverride: z.boolean(),
    extended: N,
    source: z.string(),
    payableTaskGroup: z.string().nullable(),
    lineId: N.nullable().optional(),
    sellPriceKey: z.string().nullable().optional(),
    sellPriceOverride: N.nullable().optional(),
  })
  .passthrough();

export const BidPhaseDefinitionSchema = z.object({
  id: N,
  name: z.string(),
  sortOrder: N,
});

export const PhaseQtyPriceMetaSchema = z.object({
  itemName: z.string(),
  qty: N,
  unitPrice: N,
  matched: z.boolean(),
  missingMap: z.boolean().optional(),
  missingFloorType: z.boolean().optional(),
});

export const BidModelSchema = z
  .object({
    id: N,
    name: z.string(),
    floorTypeId: N.nullable().optional(),
    floorTypeName: z.string().nullable().optional(),
    lineCount: N,
    totalPriced: N,
    materialLines: z.array(BidBreakdownLineSchema).optional(),
    laborLines: z.array(BidBreakdownLineSchema).optional(),
    unpricedLines: z.array(BidUnpricedLineSchema).optional(),
    otherCost: N.optional(),
    phases: z.record(z.string(), BidPhaseSchema),
    phaseLaborBids: z.record(z.string(), N).optional(),
    phaseLaborSellOverrideFlags: z.record(z.string(), z.boolean()).optional(),
    phaseMaterialBids: z.record(z.string(), N).optional(),
    phaseMaterialSellOverrideFlags: z.record(z.string(), z.boolean()).optional(),
    phaseQtyPrices: z.record(z.string(), N).optional(),
    phaseQtyPriceMeta: z.record(z.string(), PhaseQtyPriceMetaSchema).optional(),
    /** Catalog flat phase prices (phaseId → $). */
    divisionPhasePrices: z.record(z.string(), N).optional(),
    /** Community activation map (phaseId → 1 when Division Pricing is on). */
    divisionPhaseActive: z.record(z.string(), N).optional(),
    lineSellPrices: z.record(z.string(), N).optional(),
    salesTax: N,
    overrides: z.record(z.string(), N.nullable()).optional(),
    materialCost: N,
    bidPrice: N,
  })
  .passthrough();

export const BidBreakdownResponseSchema = z
  .object({
    modelId: N,
    modelName: z.string(),
    scope: z.string(),
    lines: z.array(BidBreakdownLineSchema),
    phases: z.record(z.string(), BidPhaseSchema),
    otherCost: N,
    salesTax: N,
    salesTaxPct: N,
    materialCost: N,
    phaseDefinitions: z.array(BidPhaseDefinitionSchema).optional(),
  })
  .passthrough();

export const BidPayloadSchema = z
  .object({
    communityId: N,
    communityName: z.string(),
    priceAsOf: z.string().optional(),
    phaseDefinitions: z.array(BidPhaseDefinitionSchema).optional(),
    margins: z.record(z.string(), N),
    salesTaxPct: N,
    models: z.array(BidModelSchema),
    totals: z
      .object({
        material: N,
        bid: N,
        priced: N,
        items: N,
      })
      .passthrough()
      .optional(),
    unpricedLines: z.array(BidUnpricedLineSchema).optional(),
    bidLocked: z.boolean().optional(),
    acceptedBid: AcceptedBidSchema,
  })
  .passthrough();

export const RevisionRowSchema = z
  .object({
    id: N,
    revision: N,
    created_at: z.string().optional(),
    pricing_file: z.string().nullable().optional(),
    priceAsOf: z.string().optional(),
    bidTotal: N.optional(),
    note: z.string().optional(),
    category: z
      .enum(['price_changes', 'cost_changes', 'mto_adjustment'])
      .nullable()
      .optional(),
    categoryLabel: z.string().nullable().optional(),
    changedItems: N.optional(),
    hasExcel: z.boolean().optional(),
    accepted: z.boolean().optional(),
    acceptedAt: z.string().nullable().optional(),
  })
  .passthrough();

export const RevisionsListSchema = z.array(RevisionRowSchema);

export const LineRowSchema = z.record(z.string(), z.unknown());

export const LinesListSchema = z.array(LineRowSchema);

export const PlanRowSchema = z
  .object({
    id: N,
    mto_file_id: N.optional(),
    model_id: N.optional(),
    filename: z.string(),
    original_name: z.string().nullable().optional(),
    uploaded_at: z.string().optional(),
  })
  .passthrough();

export const PlansListSchema = z.array(PlanRowSchema);

export const CostedLinesSchema = z.object({
  lines: z.array(z.record(z.string(), z.unknown())),
  totalCost: N,
  pricedCount: N,
  totalCount: N,
  unmappedTaskGroupCount: N.optional(),
  unmappedTaskGroups: z.array(z.string()).optional(),
});

export const MtoQuantityMatrixSchema = z.object({
  mtos: z.array(
    z.object({
      id: N,
      name: z.string(),
    })
  ),
  rows: z.array(
    z.object({
      item: z.string(),
      payable_task_group: z.string(),
      phase_name: z.string(),
      sub_phase_name: z.string(),
      quantities: z.record(z.string(), z.number().nullable()),
    })
  ),
});

export const LineOverrideHistoryRowSchema = z.object({
  id: N,
  value: N,
  effective_date: z.string(),
  updated_at: z.string().optional(),
});

export const LineOverrideHistoryListSchema = z.array(LineOverrideHistoryRowSchema);

export const PhaseSchema = z
  .object({
    id: N,
    name: z.string(),
    cost_code: z.string().nullable().optional(),
    sort_order: N.optional(),
    sub_phases: z.array(z.record(z.string(), z.unknown())).optional(),
  })
  .passthrough();

export const PhasesListSchema = z.array(PhaseSchema);

export const CustomerModelSchema = z.record(z.string(), z.unknown());

export const CustomerModelsListSchema = z.array(CustomerModelSchema);
